import { ArrowRight, ShieldOff } from "lucide-react";
import { useMemo, type ReactNode } from "react";
import { Link, useSearchParams } from "react-router-dom";

import { ExceptionsLogTable } from "../components/ExceptionsLogTable";
import { FilterBar } from "../components/FilterBar";
import { PageHeader } from "../components/PageHeader";
import { StepShell, getStep } from "../components/StepShell";
import { Badge } from "../../../ui/badge";
import { Card, CardContent } from "../../../ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../ui/select";
import { Skeleton } from "../../../ui/skeleton";
import { ToggleGroup, ToggleGroupItem } from "../../../ui/toggle-group";
import { RULE_DEFINITIONS, useExceptionsLogSummary } from "../lib/api/exceptions-log";
import type { RuleId, RuleTier, SegmentFilter } from "../lib/api/types";
import { useDashboardSearch } from "../lib/useDashboardSearch";

const SUBTITLE =
  "Every flagged deal, one row at a time, ready for LeaseWorks reconciliation.";

type PageFilters = {
  ruleTier?: "all" | RuleTier;
  rule?: string;
  status?: "all" | "candidate" | "confirmed";
};

const TIER_OPTIONS: { value: "all" | RuleTier; label: string }[] = [
  { value: "all", label: "All" },
  { value: "DQ", label: "DQ" },
  { value: "A", label: "A" },
  { value: "B", label: "B" },
  { value: "C", label: "C" },
];

const SEGMENTS: { value: SegmentFilter; label: string }[] = [
  { value: "all", label: "All" },
  { value: "PG", label: "PG" },
  { value: "Corp", label: "Corp" },
];

const STATUSES: { value: "all" | "candidate" | "confirmed"; label: string }[] = [
  { value: "all", label: "All" },
  { value: "candidate", label: "Candidate" },
  { value: "confirmed", label: "Confirmed" },
];

function EmptyState() {
  return (
    <Card className="border-neutral-inactive/40">
      <CardContent className="flex flex-col items-center gap-3 px-6 py-16 text-center">
        <ShieldOff className="size-8 text-neutral-inactive" />
        <p className="text-base font-semibold text-neutral-inactive">
          No data — not yet live on Athena
        </p>
        <p className="max-w-xl text-sm leading-relaxed text-muted-foreground">
          Deal-level exceptions are only captured once an office is live. Select the live office or
          &ldquo;All Offices&rdquo; to work the log.
        </p>
      </CardContent>
    </Card>
  );
}

function FilterLabel({ children }: { children: ReactNode }) {
  return (
    <span className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
      {children}
    </span>
  );
}

export function ExceptionsLog() {
  const { range, office, compare, segment, update } = useDashboardSearch();

  // ruleTier / rule / status are page-local filters not shared with the
  // other steps, so they're kept in the URL via a direct useSearchParams
  // call rather than extending the shared useDashboardSearch hook.
  const [searchParams, setSearchParams] = useSearchParams();
  const ruleTier = (searchParams.get("ruleTier") as "all" | RuleTier | null) ?? "all";
  const rule = searchParams.get("rule") ?? "all";
  const status = (searchParams.get("status") as "all" | "candidate" | "confirmed" | null) ?? "all";

  const patch = (next: PageFilters) => {
    setSearchParams(
      (prev) => {
        const nextParams = new URLSearchParams(prev);
        for (const [key, value] of Object.entries(next)) {
          if (value === undefined) {
            nextParams.delete(key);
          } else {
            nextParams.set(key, String(value));
          }
        }
        return nextParams;
      },
      { replace: true },
    );
  };

  const { data, isPending, isError, error } = useExceptionsLogSummary({ range, office, compare });

  const tierOf = useMemo(() => {
    const map = new Map<RuleId, RuleTier>(
      RULE_DEFINITIONS.map((d) => [d.ruleId, d.ruleTier] as const),
    );
    return (ruleId: RuleId) => map.get(ruleId) ?? "B";
  }, []);

  const rows = useMemo(() => {
    const all = data?.rows ?? [];
    return all.filter((row) => {
      if (ruleTier !== "all" && tierOf(row.ruleId) !== ruleTier) return false;
      if (rule !== "all" && row.ruleId !== rule) return false;
      // `row.pg` is null for the ~15% of applications Athena never scored, which are
      // neither PG nor Corp. `!row.pg` would have swept those into the Corp tab and
      // `row.pg` would have excluded them from PG — so a strict comparison against
      // true/false is needed to keep them out of both.
      if (segment === "PG" && row.pg !== true) return false;
      if (segment === "Corp" && row.pg !== false) return false;
      if (status !== "all" && row.reconciliationStatus !== status) return false;
      return true;
    });
  }, [data?.rows, ruleTier, rule, segment, status, tierOf]);

  return (
    <StepShell step={getStep("exceptions-log")}>
      <PageHeader title="Deal-Level Exceptions Log" subtitle={SUBTITLE} />
      <FilterBar />

      {isError ? (
        <Card>
          <CardContent className="p-6 text-sm text-decline" role="alert">
            Could not load the exceptions log: {(error as Error).message}
          </CardContent>
        </Card>
      ) : isPending || !data ? (
        <div className="space-y-4">
          <Skeleton className="h-[52px] rounded-lg" />
          <Skeleton className="h-[520px] rounded-lg" />
        </div>
      ) : !data.dataAvailable ? (
        <EmptyState />
      ) : (
        <>
          {/* Summary chip row — plain text, deliberately not a card */}
          <div className="flex flex-wrap items-center gap-3 text-sm">
            <p className="tabular-nums text-foreground">
              <span className="font-semibold text-primary">{data.openTierACount}</span> open Tier A
              flags · average{" "}
              <span className="font-semibold text-primary">{data.avgDaysUnreconciledTierA}</span>{" "}
              days unreconciled.
            </p>
            <Badge
              variant="outline"
              className="border-neutral-inactive/40 bg-neutral-inactive-dim text-neutral-inactive"
            >
              Monitored by Manage Alerts
            </Badge>
            <Link
              to="/underwriting-exceptions/manage-alerts"
              className="inline-flex items-center gap-1 bg-amber-dim px-2 py-0.5 text-xs font-medium text-primary underline-offset-4 hover:underline"
            >
              Configure
              <ArrowRight className="size-3" />
            </Link>
          </div>

          {/* Filter row */}
          <div className="flex flex-wrap items-center gap-x-6 gap-y-3 rounded-lg border border-border bg-card p-3">
            <div className="flex items-center gap-2">
              <FilterLabel>Rule Tier</FilterLabel>
              <ToggleGroup
                type="single"
                value={ruleTier}
                onValueChange={(value) => value && patch({ ruleTier: value as RuleTier | "all" })}
                variant="outline"
                size="sm"
                aria-label="Rule tier"
              >
                {TIER_OPTIONS.map((t) => (
                  <ToggleGroupItem key={t.value} value={t.value} className="px-3">
                    {t.label}
                  </ToggleGroupItem>
                ))}
              </ToggleGroup>
            </div>

            <div className="flex items-center gap-2">
              <FilterLabel>Rule</FilterLabel>
              <Select value={rule} onValueChange={(value) => patch({ rule: value })}>
                <SelectTrigger className="h-8 w-[300px] text-sm">
                  <SelectValue placeholder="All Rules" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Rules</SelectItem>
                  {RULE_DEFINITIONS.map((def) => (
                    <SelectItem key={def.ruleId} value={def.ruleId}>
                      {def.ruleId} · {def.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center gap-2">
              <FilterLabel>Segment</FilterLabel>
              <ToggleGroup
                type="single"
                value={segment}
                onValueChange={(value) => value && update({ segment: value as SegmentFilter })}
                variant="outline"
                size="sm"
                aria-label="PG / Corp segment"
              >
                {SEGMENTS.map((s) => (
                  <ToggleGroupItem key={s.value} value={s.value} className="px-3">
                    {s.label}
                  </ToggleGroupItem>
                ))}
              </ToggleGroup>
            </div>

            <div className="flex items-center gap-2">
              <FilterLabel>Reconciliation</FilterLabel>
              <ToggleGroup
                type="single"
                value={status}
                onValueChange={(value) =>
                  value && patch({ status: value as "all" | "candidate" | "confirmed" })
                }
                variant="outline"
                size="sm"
                aria-label="Reconciliation status"
              >
                {STATUSES.map((s) => (
                  <ToggleGroupItem key={s.value} value={s.value} className="px-3">
                    {s.label}
                  </ToggleGroupItem>
                ))}
              </ToggleGroup>
            </div>
          </div>

          <ExceptionsLogTable rows={rows} ruleDefinitions={RULE_DEFINITIONS} />

          <p className="text-xs leading-relaxed text-muted-foreground">
            Days unreconciled is shown as a plain count — threshold judgement lives in Manage
            Alerts, not in this table. The bell marks Tier A candidates that fall within the alert
            system&rsquo;s scope.
          </p>
        </>
      )}
    </StepShell>
  );
}
